import "./dashboard.css";

import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import React, { useEffect, useMemo, useState } from "react";

import { API_URL } from "../../../env";
import axios from "axios";
import { motion } from "framer-motion";

const COLORS = ["#0f766e", "#f97316", "#2e8bff", "#b45309", "#64748b", "#14b8a6"];

const buildFallbackMonthly = () => {
  const monthLabels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const now = new Date();

  return Array.from({ length: 6 }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
    const base = 650 + index * 80;
    const variance = (index % 2 === 0 ? 110 : 65) + (index === 5 ? 95 : 0);

    return {
      month: `${monthLabels[date.getMonth()]} ${String(date.getFullYear()).slice(-2)}`,
      amount: base + variance,
      essential: Math.round((base + variance) * 0.63),
      discretionary: Math.round((base + variance) * 0.37)
    };
  });
};

const resolvePayload = (payload) => payload?.responseData ?? payload ?? null;

const resolveDefaultAddress = (payload) => {
  const resolved = payload?.responseData ?? payload?.data ?? payload;
  if (Array.isArray(resolved)) {
    return resolved[0] || null;
  }
  return resolved || null;
};

const getWelcomeName = (profile = {}) => {
  const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(" ").trim();
  if (fullName) {
    return fullName;
  }
  return profile?.userName || profile?.name || "Customer";
};

const Dashboard = () => {
  const token = sessionStorage.getItem("token") || "";
  const userId = (sessionStorage.getItem("userId") || "").replaceAll('"', "").trim();
  const sessionUser = JSON.parse(sessionStorage.getItem("vbUser") || "{}");
  const sessionDefaultAddress = JSON.parse(sessionStorage.getItem("vbDefaultAddress") || "null");

  const [profileData, setProfileData] = useState(sessionUser);
  const [defaultAddress, setDefaultAddress] = useState(sessionDefaultAddress);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountError, setAccountError] = useState("");

  useEffect(() => {
    const loadDashboardIdentity = async () => {
      if (!token || !userId) {
        return;
      }

      setAccountLoading(true);
      setAccountError("");

      try {
        const [profileResult, defaultAddressResult] = await Promise.allSettled([
          axios.get(`${API_URL}/profile/getProfileBasedOnUserId/${encodeURIComponent(userId)}`, {
            headers: {
              env: "local",
              token
            }
          }),
          axios.get(`${API_URL}/profile/defaultAddress/${encodeURIComponent(userId)}`, {
            headers: {
              env: "local",
              token
            }
          })
        ]);

        if (profileResult.status === "fulfilled") {
          const nextProfile = resolvePayload(profileResult.value?.data);
          if (nextProfile && typeof nextProfile === "object") {
            setProfileData(nextProfile);
            sessionStorage.setItem("vbUser", JSON.stringify(nextProfile));
          }
        }

        if (defaultAddressResult.status === "fulfilled") {
          const nextDefaultAddress = resolveDefaultAddress(defaultAddressResult.value?.data);
          setDefaultAddress(nextDefaultAddress || null);

          if (nextDefaultAddress) {
            sessionStorage.setItem("vbDefaultAddress", JSON.stringify(nextDefaultAddress));
          } else {
            sessionStorage.removeItem("vbDefaultAddress");
          }
        }

        if (profileResult.status === "rejected" && defaultAddressResult.status === "rejected") {
          setAccountError("Unable to fetch profile details right now.");
        }
      } catch {
        setAccountError("Unable to fetch profile details right now.");
      } finally {
        setAccountLoading(false);
      }
    };

    loadDashboardIdentity();
  }, [token, userId]);

  const userName = useMemo(() => getWelcomeName(profileData), [profileData]);

  const defaultAddressText = useMemo(() => {
    if (!defaultAddress) {
      return "No default address selected yet.";
    }

    return [
      defaultAddress.address || defaultAddress.commpleteAddress,
      defaultAddress.city,
      defaultAddress.state,
      defaultAddress.zip
    ]
      .filter(Boolean)
      .join(", ");
  }, [defaultAddress]);

  const monthlyExpenses = useMemo(() => {
    const fromSession = JSON.parse(sessionStorage.getItem("vbMonthlyExpenses") || "[]");
    if (Array.isArray(fromSession) && fromSession.length) {
      return fromSession;
    }
    return buildFallbackMonthly();
  }, []);

  const serviceExpenses = useMemo(() => {
    const fromSession = JSON.parse(sessionStorage.getItem("vbServiceExpenses") || "[]");
    if (Array.isArray(fromSession) && fromSession.length) {
      return fromSession;
    }

    return [
      { service: "Cab Booking", value: 980 },
      { service: "Home Repair", value: 760 },
      { service: "Healthcare", value: 640 },
      { service: "Salon", value: 430 },
      { service: "Food Delivery", value: 510 }
    ];
  }, []);

  const expenditurePattern = useMemo(() => {
    const fromSession = JSON.parse(sessionStorage.getItem("vbExpensePattern") || "[]");
    if (Array.isArray(fromSession) && fromSession.length) {
      return fromSession;
    }

    return [
      { week: "Week 1", essential: 240, lifestyle: 120, urgent: 80 },
      { week: "Week 2", essential: 280, lifestyle: 150, urgent: 65 },
      { week: "Week 3", essential: 255, lifestyle: 175, urgent: 92 },
      { week: "Week 4", essential: 310, lifestyle: 165, urgent: 105 }
    ];
  }, []);

  const totalSpend = useMemo(
    () => monthlyExpenses.reduce((sum, item) => sum + Number(item.amount || 0), 0),
    [monthlyExpenses]
  );

  const avgMonthly = useMemo(() => Math.round(totalSpend / Math.max(monthlyExpenses.length, 1)), [totalSpend, monthlyExpenses]);
  const topService = useMemo(
    () => serviceExpenses.reduce((best, row) => (row.value > (best?.value || 0) ? row : best), null),
    [serviceExpenses]
  );

  const profileSignals = useMemo(() => {
    const checks = [
      Boolean(profileData?.firstName),
      Boolean(profileData?.primaryPhoneNumber || profileData?.mobile),
      Boolean(profileData?.email),
      Boolean(defaultAddress?.address || defaultAddress?.commpleteAddress)
    ];
    const completeCount = checks.filter(Boolean).length;
    const completion = Math.round((completeCount / checks.length) * 100);

    return {
      completion,
      defaultAddressReady: Boolean(defaultAddress?.address || defaultAddress?.commpleteAddress),
      providerEnabled:
        sessionStorage.getItem("vbIsProvider") === "true" ||
        Boolean(profileData?.providerId) ||
        Boolean(sessionStorage.getItem("consumerorprovideId"))
    };
  }, [defaultAddress, profileData]);

  const serviceInsights = useMemo(() => {
    const total = serviceExpenses.reduce((sum, item) => sum + Number(item.value || 0), 0);
    return [...serviceExpenses]
      .sort((a, b) => b.value - a.value)
      .slice(0, 3)
      .map((item) => ({
        ...item,
        share: total ? Math.round((item.value / total) * 100) : 0
      }));
  }, [serviceExpenses]);

  return (
    <div className="ab-dashboard-main">
      <motion.section
        className="ab-dashboard-hero"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div>
          <p className="ab-section-tag">Consumer dashboard</p>
          <h1>Welcome back, {userName}</h1>
          <p>
            Track your spending trends, compare service costs, and understand expenditure behavior with your latest profile and address details.
          </p>
          <p className="ab-default-address">
            <strong>Default address:</strong> {defaultAddressText}
          </p>
          {accountLoading && <p className="ab-dashboard-note">Refreshing profile and default address...</p>}
          {accountError && <p className="ab-dashboard-note ab-dashboard-note--error">{accountError}</p>}
        </div>

        <div className="ab-dashboard-stats">
          <article>
            <span>Total (6 months)</span>
            <strong>INR {totalSpend.toLocaleString()}</strong>
          </article>
          <article>
            <span>Average / month</span>
            <strong>INR {avgMonthly.toLocaleString()}</strong>
          </article>
          <article>
            <span>Top service</span>
            <strong>{topService?.service || "-"}</strong>
          </article>
        </div>
      </motion.section>

      <section className="ab-snapshot-grid">
        <article>
          <span>Profile completion</span>
          <strong>{profileSignals.completion}%</strong>
          <small>Based on name, phone, email, and default address.</small>
        </article>
        <article>
          <span>Default address status</span>
          <strong>{profileSignals.defaultAddressReady ? "Configured" : "Pending"}</strong>
          <small>Used for faster service discovery and checkout flow.</small>
        </article>
        <article>
          <span>Provider account</span>
          <strong>{profileSignals.providerEnabled ? "Enabled" : "Not enabled"}</strong>
          <small>Switch to provider dashboard when provider profile is active.</small>
        </article>
      </section>

      <section className="ab-charts-grid">
        <article className="ab-chart-card">
          <header>
            <h3>Expenses by month</h3>
            <p>Monthly spend trend for the latest 6-month window.</p>
          </header>
          <div className="ab-chart-wrap">
            <ResponsiveContainer width="100%" height={290}>
              <BarChart data={monthlyExpenses}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="amount" radius={[8, 8, 0, 0]} fill="#0f766e" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </article>

        <article className="ab-chart-card">
          <header>
            <h3>Expenses by services</h3>
            <p>Service-wise contribution to total expenditure.</p>
          </header>
          <div className="ab-chart-wrap">
            <ResponsiveContainer width="100%" height={290}>
              <PieChart>
                <Pie data={serviceExpenses} dataKey="value" nameKey="service" innerRadius={62} outerRadius={106} paddingAngle={4}>
                  {serviceExpenses.map((entry, index) => (
                    <Cell key={`service-${entry.service}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </article>

        <article className="ab-chart-card full-width">
          <header>
            <h3>Expenditure pattern</h3>
            <p>Weekly pattern split by essential, lifestyle, and urgent spend categories.</p>
          </header>
          <div className="ab-chart-wrap">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={expenditurePattern}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="week" />
                <YAxis />
                <Tooltip />
                <Area type="monotone" dataKey="essential" stackId="1" stroke="#0f766e" fill="#0f766e" fillOpacity={0.85} />
                <Area type="monotone" dataKey="lifestyle" stackId="1" stroke="#f97316" fill="#f97316" fillOpacity={0.82} />
                <Area type="monotone" dataKey="urgent" stackId="1" stroke="#2e8bff" fill="#2e8bff" fillOpacity={0.78} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </article>
      </section>

      <section className="ab-insights-grid">
        <article className="ab-insights-card">
          <header>
            <h3>Top spending services</h3>
            <p>Highest share contributors based on current service distribution.</p>
          </header>
          <div className="ab-insights-list">
            {serviceInsights.map((row) => (
              <div key={row.service}>
                <strong>{row.service}</strong>
                <span>INR {Number(row.value || 0).toLocaleString()} · {row.share}%</span>
              </div>
            ))}
          </div>
        </article>

        <article className="ab-insights-card">
          <header>
            <h3>Consumer recommendations</h3>
            <p>Simple guidance to improve booking speed and response quality.</p>
          </header>
          <ul className="ab-reco-list">
            <li>Keep your phone and email current for smooth provider callbacks.</li>
            <li>Review your default address details periodically for delivery accuracy.</li>
            <li>Use service-wise spend trends to plan monthly budgets better.</li>
            <li>Complete profile details to reduce request clarification delays.</li>
          </ul>
        </article>
      </section>
    </div>
  );
};

export default Dashboard;
